import { type CSSProperties, type MutableRefObject } from "react";
import type { Band } from "../../infra/api.js";
import { useCompositionStore } from "../../stores/compositionStore.js";
import type { PlaybackEngine } from "../playback/engine.js";
import { LpcAvatar } from "../library/LpcAvatar.js";
import { CrowdLayer } from "./CrowdLayer.js";
import { LIGHT_HEX, POSITIONS, SLOT_TO_STEM, moodToLightColor } from "./stage-layout.js";
import { useStageEnergy, type StageEnergy } from "./useStageEnergy.js";

interface Props {
  /** The band whose slots populate the stage. `null` → empty-stage hint. */
  band: Band | undefined;
  /** Live ref to the editor's playback engine (read-only). */
  engineRef: MutableRefObject<PlaybackEngine | null>;
}

/**
 * One stage spotlight, color from the DNA mood, brightness from global energy
 * (spec §8.1). At zero energy it falls back to the gentle `spot-pulse` CSS
 * animation (same idle look as the library stage).
 */
function LiveSpotlight({
  hex,
  position,
  energy,
  playing,
}: {
  hex: string;
  position: "left" | "right";
  energy: number;
  playing: boolean;
}): JSX.Element {
  const left = position === "left" ? "20%" : "80%";
  // 0.35 baseline → 0.85 at full energy. Idle: let the CSS pulse breathe.
  const opacity = playing ? 0.35 + energy * 0.5 : undefined;
  return (
    <div
      className="spotlight pointer-events-none absolute -top-12 h-44 w-44 -translate-x-1/2 rounded-full"
      style={{
        left,
        background: `radial-gradient(circle, ${hex}77 0%, ${hex}00 62%)`,
        opacity,
        // Suppress the idle pulse while energy drives the opacity directly.
        animationPlayState: playing ? "paused" : "running",
        transition: "opacity 90ms linear",
      }}
    />
  );
}

/** An avatar wrapper that pipes the stem's energy in as the `--energy` var. */
function LiveAvatar({
  band,
  role,
  energy,
  playing,
}: {
  band: Band;
  role: keyof typeof POSITIONS;
  energy: StageEnergy;
  playing: boolean;
}): JSX.Element | null {
  const slot = band.slots.find((s) => s.slot_role === role);
  if (!slot) return null;
  const pos = POSITIONS[role];
  const stemEnergy = energy.perStem[SLOT_TO_STEM[role]];
  // Below 0.15 the band member is essentially idle (spec §8.1) — drop to the
  // calm idle animation instead of a near-frozen play loop.
  const lively = playing && stemEnergy >= 0.15;
  const style: CSSProperties = {
    left: pos.left,
    top: pos.top,
    transform: `translate(-50%, -50%) scale(${pos.scale ?? 1})`,
    zIndex: pos.zIndex,
    // `--energy` inherits down into the avatar's keyframes (lpc-play-*).
    ["--energy" as string]: lively ? stemEnergy.toFixed(3) : "1",
  };
  return (
    <div className="absolute flex flex-col items-center" style={style}>
      <LpcAvatar
        layers={slot.artist.lpc_layers}
        size={88}
        label={slot.artist.name}
        action={lively ? (`play-${role}` as const) : "idle"}
        instrument={role}
      />
      <span className="mt-1 border border-border bg-asphalt/80 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest2 text-paper">
        {slot.artist.name}
      </span>
    </div>
  );
}

/**
 * The live stage in the song editor (Plan 13, Fase 5). Unlike the decorative
 * library `Stage` — which animates on a fixed `playing` boolean — `LiveStage`
 * is driven by `useStageEnergy`: each avatar reacts to its own stem's RMS
 * envelope, the spotlights take their color from the DNA mood and their
 * brightness from the global energy, and the crowd reacts to the same global
 * curve.
 *
 * It shares `POSITIONS` / `LIGHT_HEX` / `moodToLightColor` with the library
 * stage and the headless clip renderer through `stage-layout.ts` (D8, R1).
 *
 * Fallback: a section with no `envelope`, or a stopped transport, leaves
 * energy at zero — every avatar falls to the calm idle loop and the stage
 * still renders. Nothing crashes.
 */
export function LiveStage({ band, engineRef }: Props): JSX.Element {
  const playbackState = useCompositionStore((s) => s.playback.state);
  const dnaMood = useCompositionStore((s) => s.audioState.dnaReference?.mood ?? null);
  const energy = useStageEnergy(engineRef);
  const playing = playbackState === "playing";

  const lightColor = moodToLightColor(dnaMood);
  const lightHex = LIGHT_HEX[lightColor];
  const roles = Object.keys(POSITIONS) as (keyof typeof POSITIONS)[];

  return (
    <section aria-label="Palco ao vivo" className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <p className="catalog-num">PALCO / MONITOR</p>
        <span className="font-mono text-[10px] uppercase tracking-widest2 text-ink-muted">
          {dnaMood ? `mood · ${dnaMood}` : "mood · —"}
        </span>
      </div>

      <div
        data-testid="live-stage"
        data-playing={playing ? "true" : undefined}
        className="relative w-full overflow-hidden border border-border"
        style={{
          height: 340,
          background:
            "radial-gradient(ellipse 60% 60% at 50% 90%, rgba(232,68,58,0.30) 0%, rgba(0,0,0,0) 70%), radial-gradient(ellipse 70% 40% at 50% 20%, rgba(240,200,74,0.16) 0%, rgba(0,0,0,0) 70%), linear-gradient(180deg, #1a120c 0%, #2a1a10 50%, #150f08 100%)",
        }}
      >
        {/* Scanlines — shared CRT texture with the library stage */}
        <div className="stage-scanlines pointer-events-none absolute inset-0" />

        {/* Spotlights — color from mood, brightness from energy */}
        <LiveSpotlight hex={lightHex} position="left" energy={energy.global} playing={playing} />
        <LiveSpotlight hex={lightHex} position="right" energy={energy.global} playing={playing} />

        {/* Transport status tag */}
        <div className="absolute left-4 top-4 z-10 flex items-center gap-2">
          <span
            className={`inline-block h-1.5 w-1.5 ${
              playing ? "bg-vinyl animate-pulse" : "bg-ink-dim"
            }`}
          />
          <span className="font-mono text-[10px] uppercase tracking-widest2 text-paper">
            {playing ? "ao vivo" : "em pausa"}
          </span>
        </div>

        {/* Floor */}
        <div
          className="absolute inset-x-0 bottom-0 h-14"
          style={{
            background: "linear-gradient(180deg, #2a1a10 0%, #0a0604 100%)",
            borderTop: "1px solid #4a2a18",
            boxShadow: "inset 0 6px 10px -6px rgba(0,0,0,0.8)",
          }}
        />

        {/* Band */}
        {band &&
          roles.map((role) => (
            <LiveAvatar key={role} band={band} role={role} energy={energy} playing={playing} />
          ))}

        {/* Crowd — reacts to global energy + mood */}
        <CrowdLayer energy={energy.global} moodHex={lightHex} playing={playing} />

        {(!band || band.slots.length === 0) && (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="font-mono text-[10px] uppercase tracking-widest2 text-ink-muted">
              {band ? "palco vazio — adicione membros aos slots" : "sem banda atribuída à faixa"}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
